// @ts-nocheck
import { useRef } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import SunEditor from './suneditor';
import { tokensForDocType } from '../../constants/documentTokens';

// Rich-text editor for document sections with a placeholder token picker. The
// chips insert {{Token}} at the cursor; tokens shown depend on the doc type
// (WHS gets the auto-filled set, PTC gets the builder/subcontractor/job set,
// others get none).

// Tooltip copy for a placeholder chip. Manual tokens warn they stay literal;
// jobScoped tokens (PTC job fields) warn they're blank at connection sign-up
// and only fill in per job — so the author keeps job clauses in their own
// (strippable) sections rather than inline.
function tokenHint(t) {
  if (!t.autoFilled) return `${t.description} — not auto-filled, stays as a placeholder`;
  if (t.jobScoped) {
    return `${t.description} — blank at connection sign-up; fills in per job site`;
  }
  return t.description;
}

export default function DocEditor({ docType, content, onChange }) {
  const editorRef = useRef(null);
  const tokens = tokensForDocType(docType);
  const groups = [...new Set(tokens.map((t) => t.group))];

  const insertToken = (t) => {
    const ed = editorRef.current;
    if (ed && typeof ed.insertHTML === 'function') {
      ed.insertHTML(`{{${t.token}}}`, true, true);
    }
  };

  return (
    <Box>
      {tokens.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1.5,
            bgcolor: 'background.neutral',
            border: (theme) => `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Insert placeholder
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Click to drop a {'{{placeholder}}'} at the cursor. These are replaced with real values
            when the document is generated.
          </Typography>
          <Stack spacing={1.25}>
            {groups.map((group) => (
              <Box key={group}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontWeight: 600, mr: 1 }}
                >
                  {group}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {tokens
                    .filter((t) => t.group === group)
                    .map((t) => (
                      <Tooltip
                        key={t.token}
                        title={tokenHint(t)}
                        arrow
                      >
                        <Chip
                          label={t.label}
                          size="small"
                          onClick={() => insertToken(t)}
                          variant={t.autoFilled ? 'filled' : 'outlined'}
                          color={t.autoFilled ? 'primary' : 'default'}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <SunEditor
        setContents={content}
        onChange={onChange}
        getSunEditorInstance={(ed) => {
          editorRef.current = ed;
        }}
      />
    </Box>
  );
}
